import { styled } from '@mui/material/styles';
import Collapse from '@mui/material/Collapse';
import { Button } from '../../../Buttons/Button';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { PrimitiveAtom, useAtom } from 'jotai';

export const GraphsContainerRoot = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  position: 'relative',
}));

export const CollapseContent = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(20),
}));

export const ShowHideOverviewButton = styled(Button)(() => ({
  width: '100%',
  justifyContent: 'start',
}));

export type GraphsContainerProps = {
  openAtom: PrimitiveAtom<boolean>;
  children: React.ReactNode;
  slots?: {
    button?: (props: { open: boolean; toggle: () => void }) => React.ReactNode;
  };
};

type GraphsContainerButtonProps = {
  onClick: () => void;
  open: boolean;
  openText?: string;
  closeText?: string;
};

export const GraphsContainerButton = ({
  onClick,
  open,
  openText,
  closeText,
}: GraphsContainerButtonProps) => {
  const { t } = useTranslation();
  const openLabel = openText ?? t('layout.graphsContainer.showOverview');
  const closeLabel = closeText ?? t('layout.graphsContainer.hideOverview');
  return (
    <ShowHideOverviewButton
      variant="tertiary"
      onClick={onClick}
      iconStart={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
    >
      <Typography variant="text1">{open ? closeLabel : openLabel}</Typography>
    </ShowHideOverviewButton>
  );
};

const GraphsContainerLayout = ({
  open,
  children,
  slots,
  toggle,
}: Omit<GraphsContainerProps, 'openAtom'> & {
  open: boolean;
  toggle: () => void;
}) => {
  return (
    <GraphsContainerRoot>
      {slots?.button ? (
        slots?.button({ open, toggle })
      ) : (
        <Box>
          <GraphsContainerButton onClick={toggle} open={open} />
        </Box>
      )}
      <Collapse in={open}>
        <CollapseContent>
          <div role="region" aria-label="Graphs">
            {children}
          </div>
        </CollapseContent>
      </Collapse>
    </GraphsContainerRoot>
  );
};

export const GraphsContainer = ({ openAtom, children, slots }: GraphsContainerProps) => {
  const [open, setOpen] = useAtom(openAtom);

  const toggle = () => {
    setOpen((open) => !open);
  };

  return (
    <GraphsContainerLayout slots={slots} open={open} toggle={toggle}>
      {children}
    </GraphsContainerLayout>
  );
};
